'use client'

import { format, isValid, parseISO } from 'date-fns'

import { useEffect, useMemo, useState } from 'react'

import { Link } from '@/i18n/navigation'

const WEEKS_PER_YEAR = 52
const MS_PER_WEEK = 7 * 24 * 60 * 60 * 1000
const MIN_LIFESPAN = 60
const MAX_LIFESPAN = 120

const MILESTONES = [
  { age: 18, label: 'Adulthood' },
  { age: 22, label: 'College Graduation' },
  { age: 25, label: 'Quarter Century' },
  { age: 30, label: 'Thirties' },
  { age: 40, label: 'Forties' },
  { age: 50, label: 'Half Century' },
  { age: 65, label: 'Retirement Age' },
]

const EXAMPLE_YEARS = [1980, 1990, 1995, 2000]

// Grid geometry, in SVG user units
const PITCH = 12
const CELL = 9
const GRID_W = WEEKS_PER_YEAR * PITCH
const GRID_X = 34
const GRID_Y = 20
const LABELS_W = 172

/** True below the width where the milestone labels beside the grid get too small to read. */
function useCompact() {
  const [compact, setCompact] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 720px)')
    const update = () => setCompact(mq.matches)
    update()
    mq.addEventListener('change', update)
    return () => mq.removeEventListener('change', update)
  }, [])
  return compact
}

interface Hover {
  week: number
  row: number
  col: number
}

interface LifeGridProps {
  birthDate: Date
  lifespan: number
  /** Weeks lived so far, or null before the current date is known. */
  lived: number | null
  showMilestones: boolean
}

function LifeGrid({
  birthDate,
  lifespan,
  lived,
  showMilestones,
}: Readonly<LifeGridProps>) {
  const compact = useCompact()
  const [hover, setHover] = useState<Hover | null>(null)

  const total = lifespan * WEEKS_PER_YEAR
  const gridH = lifespan * PITCH
  const done = lived ?? 0
  const fullRows = Math.floor(done / WEEKS_PER_YEAR)
  const partial = done % WEEKS_PER_YEAR
  const hasCurrent = lived !== null && lived < total
  // Rows below the one that holds the current week
  const aheadFrom = fullRows + (hasCurrent || partial > 0 ? 1 : 0)

  const milestones = showMilestones
    ? MILESTONES.filter(m => m.age < lifespan)
    : []
  const width = GRID_X + GRID_W + (compact ? 4 : LABELS_W)
  const height = GRID_Y + gridH + 10

  const yearLabels: number[] = []
  for (let y = 0; y < lifespan; y += 10) yearLabels.push(y)

  const handleMove = (e: React.PointerEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const x = ((e.clientX - rect.left) / rect.width) * width - GRID_X
    const y = ((e.clientY - rect.top) / rect.height) * height - GRID_Y
    const col = Math.floor(x / PITCH)
    const row = Math.floor(y / PITCH)
    if (col < 0 || col >= WEEKS_PER_YEAR || row < 0 || row >= lifespan) {
      setHover(null)
      return
    }
    setHover({ week: row * WEEKS_PER_YEAR + col, row, col })
  }

  const hoverDate = hover
    ? new Date(birthDate.getTime() + hover.week * MS_PER_WEEK)
    : null

  return (
    <div className="plot">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={
          lived === null
            ? `A grid of ${lifespan} rows of 52 weeks.`
            : `A grid of ${lifespan} rows of 52 weeks. ${done.toLocaleString('en-US')} weeks are filled as lived and ${(total - done).toLocaleString('en-US')} are ahead.`
        }
        onPointerMove={handleMove}
        onPointerLeave={() => setHover(null)}
      >
        <defs>
          <pattern
            id="l-weeks-lived"
            width={PITCH}
            height={PITCH}
            patternUnits="userSpaceOnUse"
          >
            <rect className="lived" width={CELL} height={CELL} rx="1.5" />
          </pattern>
          <pattern
            id="l-weeks-ahead"
            width={PITCH}
            height={PITCH}
            patternUnits="userSpaceOnUse"
          >
            <rect
              className="ahead"
              x="0.5"
              y="0.5"
              width={CELL - 1}
              height={CELL - 1}
              rx="1.5"
            />
          </pattern>
        </defs>

        <g className="axis">
          {[1, 13, 26, 39, 52].map(w => (
            <text key={w} x={GRID_X + (w - 1) * PITCH} y="10">
              {w}
            </text>
          ))}
          {yearLabels.map(y => (
            <text
              key={y}
              x={GRID_X - 10}
              y={GRID_Y + y * PITCH + 8}
              textAnchor="end"
            >
              {y}
            </text>
          ))}
        </g>

        <g transform={`translate(${GRID_X} ${GRID_Y})`}>
          {fullRows > 0 && (
            <rect
              width={GRID_W}
              height={fullRows * PITCH}
              fill="url(#l-weeks-lived)"
            />
          )}
          {partial > 0 && (
            <rect
              y={fullRows * PITCH}
              width={partial * PITCH}
              height={PITCH}
              fill="url(#l-weeks-lived)"
            />
          )}
          {hasCurrent && (
            <>
              <rect
                className="now"
                x={partial * PITCH}
                y={fullRows * PITCH}
                width={CELL}
                height={CELL}
                rx="1.5"
              />
              {partial < WEEKS_PER_YEAR - 1 && (
                <rect
                  x={(partial + 1) * PITCH}
                  y={fullRows * PITCH}
                  width={(WEEKS_PER_YEAR - partial - 1) * PITCH}
                  height={PITCH}
                  fill="url(#l-weeks-ahead)"
                />
              )}
            </>
          )}
          {aheadFrom < lifespan && (
            <rect
              y={aheadFrom * PITCH}
              width={GRID_W}
              height={(lifespan - aheadFrom) * PITCH}
              fill="url(#l-weeks-ahead)"
            />
          )}

          {milestones.map(m => (
            <circle
              key={m.age}
              className="ring"
              cx={CELL / 2}
              cy={m.age * PITCH + CELL / 2}
              r="7"
            />
          ))}

          {!compact && (
            <g className="labels">
              {milestones
                // keep clear of the "you are here" label
                .filter(m => !hasCurrent || Math.abs(m.age - fullRows) > 1)
                .map(m => (
                  <text key={m.age} x={GRID_W + 12} y={m.age * PITCH + 8}>
                    {m.age} · {m.label}
                  </text>
                ))}
              {hasCurrent && (
                <text className="here" x={GRID_W + 12} y={fullRows * PITCH + 8}>
                  {fullRows} · You are here
                </text>
              )}
            </g>
          )}

          {hover && (
            <rect
              className="cursor"
              x={hover.col * PITCH - 1.5}
              y={hover.row * PITCH - 1.5}
              width={CELL + 3}
              height={CELL + 3}
              rx="2.5"
            />
          )}
        </g>
      </svg>

      {hover && hoverDate && (
        <div
          className="tip"
          style={{
            left: `${((GRID_X + hover.col * PITCH + CELL / 2) / width) * 100}%`,
            top: `${((GRID_Y + hover.row * PITCH) / height) * 100}%`,
          }}
        >
          <b>
            Age {hover.row}, week {hover.col + 1}
          </b>{' '}
          · {format(hoverDate, 'MMM d, yyyy')}
        </div>
      )}

      {compact && milestones.length > 0 && (
        <ul className="milestones">
          {milestones.map(m => (
            <li key={m.age}>
              <span>{m.age}</span>
              {m.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export function MyLifeInWeeksContent() {
  // Demo birth date: a relatable example, someone born June 15, 1990
  const [birthDate, setBirthDate] = useState<Date>(() => new Date(1990, 5, 15))
  const [lifespan, setLifespan] = useState(80)
  const [showMilestones, setShowMilestones] = useState(true)
  // The current date is only known in the browser, so it is set after mount
  const [now, setNow] = useState<Date | null>(null)
  useEffect(() => setNow(new Date()), [])

  const stats = useMemo(() => {
    const total = lifespan * WEEKS_PER_YEAR
    if (!now) return { total, lived: null }
    const weeks = Math.floor(
      (now.getTime() - birthDate.getTime()) / MS_PER_WEEK
    )
    const lived = Math.min(Math.max(weeks, 0), total)
    return { total, lived }
  }, [birthDate, lifespan, now])

  const { total, lived } = stats
  const count = (n: number | null) =>
    n === null ? '–' : n.toLocaleString('en-US')

  const handleBirthDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const date = parseISO(e.target.value)
    if (isValid(date)) setBirthDate(date)
  }

  const handleLifespanChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const years = Math.round(Number(e.target.value))
    if (years >= MIN_LIFESPAN && years <= MAX_LIFESPAN) setLifespan(years)
  }

  return (
    <div className="landing-root">
      <div className="l-weeks">
        <div className="wrap">
          <header className="head">
            <div className="k">
              <Link href="/demos">Demos</Link>
              <span aria-hidden="true">/</span>
              <span>Visualization</span>
            </div>
            <h1>
              My Life in Weeks<span aria-hidden="true">.</span>
            </h1>
            <p className="lede">
              This visualization shows an entire life as a grid of weeks. Each
              small box represents one week. The filled boxes are weeks already
              lived, and the empty ones are what remains (hopefully).
            </p>
          </header>

          <section className="main">
            <aside className="rail">
              <dl className="stats">
                <div>
                  <dt>Age</dt>
                  <dd>
                    {lived === null ? '–' : Math.floor(lived / WEEKS_PER_YEAR)}
                  </dd>
                </div>
                <div>
                  <dt>Lived</dt>
                  <dd className="accent">
                    {lived === null
                      ? '–'
                      : `${((lived / total) * 100).toFixed(1)}%`}
                  </dd>
                </div>
                <div className="sm">
                  <dt>Weeks lived</dt>
                  <dd>{count(lived)}</dd>
                </div>
                <div className="sm">
                  <dt>Weeks ahead</dt>
                  <dd>{count(lived === null ? null : total - lived)}</dd>
                </div>
              </dl>

              <form className="panel" onSubmit={e => e.preventDefault()}>
                <div className="k">Try your own</div>
                <div className="field">
                  <label htmlFor="demo-birth-date">Birth date</label>
                  <input
                    id="demo-birth-date"
                    type="date"
                    value={format(birthDate, 'yyyy-MM-dd')}
                    onChange={handleBirthDateChange}
                  />
                </div>
                <div className="field">
                  <label htmlFor="demo-lifespan">
                    Expected lifespan (years)
                  </label>
                  <input
                    id="demo-lifespan"
                    type="number"
                    min={MIN_LIFESPAN}
                    max={MAX_LIFESPAN}
                    defaultValue={lifespan}
                    onChange={handleLifespanChange}
                  />
                  <span className="hint">
                    Global average is around 72–80 years
                  </span>
                </div>
                <div className="field">
                  <span className="lbl" id="demo-examples">
                    Quick examples
                  </span>
                  <div
                    className="pills"
                    role="group"
                    aria-labelledby="demo-examples"
                  >
                    {EXAMPLE_YEARS.map(year => (
                      <button
                        key={year}
                        type="button"
                        aria-pressed={birthDate.getFullYear() === year}
                        onClick={() => setBirthDate(new Date(year, 5, 15))}
                      >
                        {year}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="toggle">
                  <span className="lbl" id="demo-milestones">
                    Life milestones
                  </span>
                  <button
                    type="button"
                    aria-pressed={showMilestones}
                    aria-labelledby="demo-milestones"
                    onClick={() => setShowMilestones(!showMilestones)}
                  >
                    {showMilestones ? 'Shown' : 'Hidden'}
                  </button>
                </div>
              </form>

              <ul className="legend">
                <li>
                  <i className="lived" aria-hidden="true" />A week already lived
                </li>
                <li>
                  <i className="now" aria-hidden="true" />
                  This week
                </li>
                <li>
                  <i className="ahead" aria-hidden="true" />A week ahead
                </li>
                <li>
                  <i className="ring" aria-hidden="true" />A milestone year
                </li>
                <li className="note">Each row is one year, 52 weeks.</li>
              </ul>
            </aside>

            <div className="grid-col">
              <div className="grid-top">
                <span>
                  Born {format(birthDate, 'MMMM d, yyyy')} · {lifespan}-year
                  life
                </span>
                <span className="n">{total.toLocaleString('en-US')} weeks</span>
              </div>
              <LifeGrid
                birthDate={birthDate}
                lifespan={lifespan}
                lived={lived}
                showMilestones={showMilestones}
              />
            </div>
          </section>

          <section className="more">
            <div>
              <div className="k">01 — The power of this view</div>
              <ul className="points">
                <li>Makes time feel tangible and finite</li>
                <li>Puts life events into perspective</li>
                <li>Encourages intentional living</li>
                <li>Helps prioritize what matters</li>
                <li>Motivates action on goals</li>
                <li>Shows how much time different life phases take</li>
              </ul>
            </div>
            <div className="side">
              <div>
                <div className="k">02 — Remember</div>
                <p className="remember">
                  This is just an approximation. Quality matters more than
                  quantity. Each week is an opportunity to create meaning, build
                  relationships, learn something new, or make a positive impact.
                  The goal isn&apos;t to rush through life, but to live it
                  intentionally.
                </p>
              </div>
              <div className="full">
                <h2>The full version includes</h2>
                <p>
                  Personal profile integration, custom milestone tracking,
                  export, and personal data persistence.
                </p>
                <Link href="/apps/my-life-in-weeks">Open the full app</Link>
              </div>
              <p className="credit">
                Inspired by Tim Urban&apos;s “Your Life in Weeks”.
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}
